import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import LogoMark from '../LogoMark';

const APP_STORE_URL  = 'https://apps.apple.com/us/app/fundpot/id6776429234';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.aramsarkisyan.FundPotApp';
const FUNCTIONS_BASE = 'https://us-central1-fundpot-app.cloudfunctions.net';

// Same live publishable key the app uses (safe to expose — it can only create
// payments, never move money).
const stripePromise = loadStripe('pk_live_51TeM7dBO6HXKsfIYKpY1eKfIKdMSNkU2HzVMDjTHzMbSkkczCQKHTWsrmyWU0FSm4mdpDqPtEKSO8qZCV6aFowR800we87PRNL');

// Match the site's dark glass look inside Stripe's iframe.
const stripeAppearance = {
  theme: 'night',
  variables: {
    colorPrimary:   '#2BE3A4',
    colorBackground: '#101413',
    colorText:      '#F2F5F4',
    borderRadius:   '12px',
    fontFamily:     '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
};

/* ─── styles ─── */
const s = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' },
  wrap: { width: '100%', maxWidth: 1080, padding: '0 24px 40px', position: 'relative', zIndex: 1 },

  nav:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '28px 0 0' },
  navBrand:    { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'var(--text)', cursor: 'pointer' },
  navWordmark: { fontSize: 21, fontWeight: 800, letterSpacing: -0.6 },
  navLink:     { color: 'var(--subtext)', textDecoration: 'none', fontSize: 14.5, fontWeight: 600 },

  loader: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh', color: 'var(--subtext)', fontSize: 16 },

  header:   { padding: '44px 0 34px' },
  fundName: { fontSize: 'clamp(34px, 4.4vw, 52px)', fontWeight: 800, letterSpacing: -1.6, lineHeight: 1.08, margin: '14px 0 10px' },
  fundDesc: { color: 'var(--subtext)', fontSize: 17, lineHeight: 1.6, maxWidth: 560 },

  card:      { borderRadius: 24, padding: 30 },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 16.5, fontWeight: 800, letterSpacing: -0.2, marginBottom: 18 },
  iconChip:  {
    width: 34, height: 34, borderRadius: 11, background: 'var(--accentDim)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0,
  },

  balLabel: { fontSize: 12.5, color: 'var(--subtext)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.4 },
  balAmt:   { fontSize: 'clamp(46px, 5vw, 60px)', fontWeight: 800, letterSpacing: -2, margin: '8px 0 4px' },
  barWrap:  { height: 10, background: 'var(--border)', borderRadius: 5, overflow: 'hidden', marginTop: 16 },
  barFill:  {
    height: '100%', borderRadius: 5, transition: 'width .6s cubic-bezier(0.16, 1, 0.3, 1)',
    background: 'linear-gradient(90deg, #2BE3A4 0%, #12BD87 100%)', boxShadow: '0 0 14px rgba(43,227,164,0.5)',
  },
  goalRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10, fontSize: 13.5, color: 'var(--subtext)' },
  goalPct:  { fontWeight: 800, color: 'var(--accent)' },

  banner: {
    display: 'flex', alignItems: 'center', gap: 12, borderRadius: 18, padding: '16px 20px',
    background: 'var(--accentDim)', border: '1px solid rgba(43,227,164,0.3)',
    color: 'var(--accent)', fontWeight: 700, fontSize: 15,
  },
  bannerMuted: {
    display: 'flex', alignItems: 'center', gap: 12, borderRadius: 18, padding: '16px 20px',
    background: 'var(--inputBg)', border: '1px solid var(--border)',
    color: 'var(--subtext)', fontWeight: 600, fontSize: 14.5,
  },

  zelleVal: { fontSize: 26, fontWeight: 800, letterSpacing: -0.5, marginBottom: 18 },
  copyBtn: {
    width: '100%', background: 'linear-gradient(135deg, #2BE3A4 0%, #12BD87 100%)', color: '#04140F',
    border: 'none', borderRadius: 14, padding: 16, fontSize: 15.5, fontWeight: 800,
    cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 10px 28px rgba(43,227,164,0.28)',
  },
  memoChip: {
    display: 'block', background: 'var(--inputBg)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '12px 16px', marginTop: 14, fontSize: 13.5,
    color: 'var(--subtext)', lineHeight: 1.6,
  },
  memoCode: { fontWeight: 800, color: 'var(--accent)', letterSpacing: 0.5 },
  note:     { fontSize: 13, color: 'var(--subtext)', lineHeight: 1.65, marginTop: 14 },

  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--subtext)', textTransform: 'uppercase', letterSpacing: 1.4, marginBottom: 8, display: 'block' },
  input: {
    width: '100%', background: 'var(--inputBg)', border: '1px solid var(--border)', borderRadius: 14,
    padding: '15px 17px', color: 'var(--text)', fontSize: 16,
    outline: 'none', fontFamily: 'inherit', marginBottom: 18, boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%', background: 'linear-gradient(135deg, #2BE3A4 0%, #12BD87 100%)', color: '#04140F',
    border: 'none', borderRadius: 14, padding: 17, fontSize: 16, fontWeight: 800,
    cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 10px 28px rgba(43,227,164,0.28)',
  },
  submitBtnDisabled: { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' },
  formErr:  { color: 'var(--danger)', fontSize: 14, marginBottom: 14 },
  successMsg: {
    display: 'flex', alignItems: 'center', gap: 12, borderRadius: 14, padding: '18px 20px',
    background: 'var(--accentDim)', border: '1px solid rgba(43,227,164,0.3)',
    color: 'var(--accent)', fontWeight: 700, fontSize: 15,
  },

  countChip: {
    marginLeft: 'auto', background: 'var(--accentDim)', color: 'var(--accent)',
    borderRadius: 999, padding: '4px 12px', fontSize: 13, fontWeight: 800,
  },
  subHead: {
    fontSize: 12, fontWeight: 800, color: 'var(--subtext)', textTransform: 'uppercase',
    letterSpacing: 1.3, margin: '4px 0 10px',
  },
  rankChip: {
    width: 30, height: 30, borderRadius: 10, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0, color: '#04140F',
  },
  rankColors: [
    { background: 'linear-gradient(135deg, #FCE27E 0%, #DB9C2C 100%)', boxShadow: '0 4px 12px rgba(219,156,44,0.35)' },
    { background: 'linear-gradient(135deg, #E8E8EC 0%, #A8ADB8 100%)', boxShadow: '0 4px 12px rgba(168,173,184,0.3)' },
    { background: 'linear-gradient(135deg, #E5A46B 0%, #B26B35 100%)', boxShadow: '0 4px 12px rgba(178,107,53,0.3)' },
  ],
  topDivider: { borderBottom: '1px solid var(--border)', margin: '16px 0' },
  showAllBtn: {
    width: '100%', marginTop: 14, background: 'transparent', color: 'var(--accent)',
    border: '1px solid var(--border)', borderRadius: 12, padding: '11px 16px',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  contribRow:  { display: 'flex', alignItems: 'center', gap: 14, padding: '13px 0', borderBottom: '1px solid var(--border)' },
  avatar: {
    width: 38, height: 38, borderRadius: 13, background: 'var(--accentDim)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--accent)', fontWeight: 800, fontSize: 15, flexShrink: 0,
  },
  contribName: { fontSize: 15, color: 'var(--text)', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  contribAmt:  { marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: 'var(--accent)', whiteSpace: 'nowrap' },
  emptyText:   { color: 'var(--subtext)', fontSize: 14.5, lineHeight: 1.6 },

  // Prominent get-the-app strip directly under the header. On mobile the page
  // collapses to one column, so anything in the right column lands at the very
  // bottom — this keeps both store badges above the fold.
  getAppTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 18, flexWrap: 'wrap', borderRadius: 20, padding: '18px 22px', marginBottom: 26,
  },
  getAppCopy:  { minWidth: 0 },
  getAppTitle: { fontSize: 15.5, fontWeight: 800, letterSpacing: -0.2, marginBottom: 3 },
  getAppSub:   { fontSize: 13.5, color: 'var(--subtext)', lineHeight: 1.5 },
  getAppBadges:{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  appCard:   { display: 'flex', flexDirection: 'column', gap: 14 },
  appText:   { fontSize: 14.5, color: 'var(--subtext)', lineHeight: 1.6 },
  badgeRow:  { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },

  footer: { borderTop: '1px solid var(--border)', padding: '34px 24px', textAlign: 'center', color: 'var(--subtext)', fontSize: 14, width: '100%', position: 'relative', zIndex: 1 },

  err404:      { textAlign: 'center', padding: '120px 24px 80px' },
  err404Title: { fontSize: 30, fontWeight: 800, letterSpacing: -0.8, margin: '22px 0 8px' },
  err404Sub:   { color: 'var(--subtext)', fontSize: 15.5 },
  err404Btn: {
    marginTop: 28, background: 'linear-gradient(135deg, #2BE3A4 0%, #12BD87 100%)', color: '#04140F',
    border: 'none', borderRadius: 14, padding: '14px 30px', fontSize: 15.5, fontWeight: 800,
    cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 10px 28px rgba(43,227,164,0.28)',
  },
};

/* ─── inline icons (no emoji) ─── */
const Ic = {
  card:   <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2" /><path d="M2 10h20" /></svg>,
  send:   <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4Z" /></svg>,
  pen:    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
  users:  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  check:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>,
  lock:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  search: <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--subtext)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>,
  phone:  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" /><path d="M12 18h.01" /></svg>,
};


// iOS hands a freshly-installed app nothing about the link that sent someone to
// the App Store, so stash the fund link on the clipboard on the way out. The app
// checks for it on first launch and offers to open the right fund.
function stashInvite(code) {
  try { navigator.clipboard?.writeText(`https://fundpot.app/fund/${code}`); } catch {}
}

const money = n => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// iOS gets Apple's Smart App Banner from a meta tag (injected by the
// fundPreview Cloud Function). Android has no OS equivalent — Chrome dropped
// native install banners — so render our own, only on Android.
function AndroidAppBar({ onGet }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua) && sessionStorage.getItem('fp_hide_appbar') !== '1') setShow(true);
  }, []);
  if (!show) return null;
  return (
    <div style={s.androidBar}>
      <img src="/fundpot-icon.png" alt="" style={s.abIcon} />
      <span style={s.abText}>
        <div style={s.abName}>FundPot</div>
        <div style={s.abSub}>Chat, polls &amp; instant alerts</div>
      </span>
      <a style={s.abBtn} href={PLAY_STORE_URL} target="_blank" rel="noreferrer" onClick={onGet}>Get</a>
      <button
        style={s.abClose}
        aria-label="Dismiss"
        onClick={() => { sessionStorage.setItem('fp_hide_appbar','1'); setShow(false); }}
      >×</button>
    </div>
  );
}

/* ─── card / Apple Pay / Google Pay checkout ─── */

const payStyles = {
  checkRow:  { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, cursor: 'pointer', userSelect: 'none' },
  checkbox:  { width: 18, height: 18, accentColor: '#2BE3A4', cursor: 'pointer' },
  checkText: { fontSize: 14, color: 'var(--subtext)' },
  feeBox: {
    background: 'var(--inputBg)', border: '1px solid var(--border)', borderRadius: 12,
    padding: '12px 16px', marginBottom: 16, fontSize: 13.5, color: 'var(--subtext)', lineHeight: 1.7,
  },
  feeStrong: { color: 'var(--text)', fontWeight: 700 },
  backLink: {
    background: 'none', border: 'none', color: 'var(--subtext)', fontSize: 13.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginTop: 14,
  },
  divider:     { display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' },
  // Android install bar — Google removed Chrome's native app-install banner, so
  // this hand-rolls the equivalent of iOS's Smart App Banner for parity.
  androidBar: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'var(--card)', borderBottom: '1px solid var(--border)',
    padding: '10px 16px', position: 'sticky', top: 0, zIndex: 20,
    backdropFilter: 'blur(12px)',
  },
  abIcon:  { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'block' },
  abText:  { minWidth: 0, flex: 1, lineHeight: 1.3 },
  abName:  { fontSize: 14.5, fontWeight: 800, letterSpacing: -0.2 },
  abSub:   { fontSize: 12.5, color: 'var(--subtext)' },
  abBtn: {
    flexShrink: 0, background: 'linear-gradient(135deg, #2BE3A4 0%, #12BD87 100%)',
    color: '#04140F', border: 'none', borderRadius: 999, padding: '8px 20px',
    fontSize: 14, fontWeight: 800, textDecoration: 'none', fontFamily: 'inherit',
  },
  abClose: {
    flexShrink: 0, background: 'none', border: 'none', color: 'var(--subtext)',
    fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '0 2px', fontFamily: 'inherit',
  },
  dividerLine: { flex: 1, height: 1, background: 'var(--border)' },
  dividerText: { fontSize: 12.5, fontWeight: 700, color: 'var(--subtext)', textTransform: 'uppercase', letterSpacing: 1 },
};

// Inner form — must live inside <Elements> to use the Stripe hooks.
// Wallets (Apple Pay / Google Pay) render as big native buttons up top via the
// Express Checkout Element; the card form sits below an "or pay with card"
// divider. The PaymentElement's own inline wallet rows are disabled so the
// same wallet never appears twice.
function PayInner({ totalAmount, onPaid }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying,  setPaying]  = useState(false);
  const [err,     setErr]     = useState('');
  const [hasWallet, setHasWallet] = useState(false);

  async function confirmNow() {
    if (!stripe || !elements) return;
    setPaying(true);
    setErr('');
    // Wallets and cards stay on-page; redirect-based methods return to this
    // URL, where the payment_intent query param finishes the confirmation.
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href.split('?')[0] },
      redirect: 'if_required',
    });
    if (result.error) {
      setErr(result.error.message || 'Payment failed. Try again.');
      setPaying(false);
      return;
    }
    if (result.paymentIntent?.status === 'succeeded') {
      await onPaid(result.paymentIntent.id);
    }
    setPaying(false);
  }

  function handlePay(e) {
    e.preventDefault();
    confirmNow();
  }

  return (
    <form onSubmit={handlePay}>
      <ExpressCheckoutElement
        onReady={e => setHasWallet((e.availablePaymentMethods && Object.values(e.availablePaymentMethods).some(Boolean)) || false)}
        onConfirm={confirmNow}
        options={{
          paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never' },
          buttonHeight: 48,
        }}
      />
      {hasWallet && (
        <div style={payStyles.divider}>
          <span style={payStyles.dividerLine} />
          <span style={payStyles.dividerText}>or pay with card</span>
          <span style={payStyles.dividerLine} />
        </div>
      )}
      <PaymentElement options={{ wallets: { applePay: 'never', googlePay: 'never' } }} />
      {err && <p style={{ ...s.formErr, marginTop: 14 }}>{err}</p>}
      <button
        type="submit"
        className={paying ? '' : 'cta-primary'}
        style={{ ...s.submitBtn, marginTop: 18, ...(paying ? s.submitBtnDisabled : {}) }}
        disabled={paying || !stripe}
      >
        {paying ? 'Processing…' : `Pay ${money(totalAmount)}`}
      </button>
    </form>
  );
}

function WebPay({ fund, onSuccess }) {
  const [phase,  setPhase]  = useState('form'); // form → pay → done
  const [name,   setName]   = useState('');
  const [amount, setAmount] = useState('');
  const [anon,   setAnon]   = useState(false);
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');
  const [intent, setIntent] = useState(null); // { clientSecret, totalAmount, baseAmount, fee }

  async function handleContinue(e) {
    e.preventDefault();
    setErr('');
    const amt = parseFloat(amount);
    if (!name.trim()) { setErr('Enter your name.'); return; }
    if (!amount || isNaN(amt) || amt < 1) { setErr('Enter a valid amount ($1 minimum).'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/createWebContributionPayment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ code: fund.shortCode, name: name.trim(), amountDollars: amt, anonymous: anon }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.message || data.error || 'Something went wrong.'); return; }
      setIntent(data);
      setPhase('pay');
    } catch {
      setErr('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePaid(paymentIntentId) {
    try {
      await fetch(`${FUNCTIONS_BASE}/confirmWebContributionPayment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ paymentIntentId }),
      });
    } catch {}
    setPhase('done');
    onSuccess();
  }

  if (phase === 'done') return (
    <div style={s.successMsg}>{Ic.check} Thank you! Your contribution has been added to the fund.</div>
  );

  if (phase === 'pay' && intent) return (
    <>
      <div style={payStyles.feeBox}>
        The fund receives <span style={payStyles.feeStrong}>{money(intent.baseAmount)}</span> ·
        processing &amp; service fee <span style={payStyles.feeStrong}>{money(intent.fee)}</span> ·
        you pay <span style={payStyles.feeStrong}>{money(intent.totalAmount)}</span>
      </div>
      <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret, appearance: stripeAppearance }}>
        <PayInner totalAmount={intent.totalAmount} onPaid={handlePaid} />
      </Elements>
      <button style={payStyles.backLink} onClick={() => { setPhase('form'); setIntent(null); }}>
        ← Change amount
      </button>
    </>
  );

  return (
    <form onSubmit={handleContinue}>
      {err && <p style={s.formErr}>{err}</p>}
      <label style={s.label}>Your Name</label>
      <input
        className="field" style={s.input} placeholder="First and last name"
        value={name} onChange={e => setName(e.target.value)}
      />
      <label style={s.label}>Amount ($)</label>
      <input
        className="field" style={s.input} placeholder="50" type="number" min="1" step="0.01"
        value={amount} onChange={e => setAmount(e.target.value)}
      />
      <label style={payStyles.checkRow}>
        <input type="checkbox" style={payStyles.checkbox} checked={anon} onChange={e => setAnon(e.target.checked)} />
        <span style={payStyles.checkText}>Contribute anonymously</span>
      </label>
      <button
        type="submit"
        className={busy ? '' : 'cta-primary'}
        style={{ ...s.submitBtn, ...(busy ? s.submitBtnDisabled : {}) }}
        disabled={busy}
      >
        {busy ? 'One moment…' : 'Continue to Payment'}
      </button>
    </form>
  );
}

export default function Fund() {
  const { code }   = useParams();
  const navigate   = useNavigate();

  const [fund,          setFund]          = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [notFound,      setNotFound]      = useState(false);

  const [copied,  setCopied]  = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [returnedPaid, setReturnedPaid] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // Firestore locks fund reads to signed-in members, so the public web
      // view goes through getPublicFund, which returns only safe fields.
      const res  = await fetch(`${FUNCTIONS_BASE}/getPublicFund?code=${encodeURIComponent(code.toUpperCase())}`);
      if (!res.ok) { setNotFound(true); setLoading(false); return; }
      const data = await res.json();
      setFund(data.fund);
      setContributions(data.contributions || []);
    } catch (e) {
      console.error(e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { refresh(); }, [refresh]);

  // Redirect-based payment methods return here with ?payment_intent=…&redirect_status=…
  // Finish recording the contribution, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const piId = params.get('payment_intent');
    if (piId && params.get('redirect_status') === 'succeeded') {
      (async () => {
        try {
          await fetch(`${FUNCTIONS_BASE}/confirmWebContributionPayment`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ paymentIntentId: piId }),
          });
        } catch {}
        setReturnedPaid(true);
        window.history.replaceState({}, '', window.location.pathname);
        refresh();
      })();
    }
  }, [refresh]);

  function handleCopyZelle() {
    navigator.clipboard.writeText(fund.zelleInfo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (loading) return (
    <div style={s.page}>
      <div className="mesh" />
      <div style={s.loader}>Loading fund…</div>
    </div>
  );

  if (notFound) return (
    <div style={s.page}>
      <div className="mesh" />
      <div style={{ ...s.wrap, ...s.err404 }}>
        {Ic.search}
        <h2 style={s.err404Title}>Fund not found</h2>
        <p style={s.err404Sub}>Check the code and try again.</p>
        <button className="cta-primary" onClick={() => navigate('/')} style={s.err404Btn}>
          Try another code
        </button>
      </div>
    </div>
  );

  const progress = fund.goal > 0 ? Math.min((fund.balance || 0) / fund.goal, 1) : 0;
  const isAnon   = fund.anonymousContributors === true;
  const isActive = fund.status === 'active';

  // Leaderboard: aggregate per person (mirrors the app's People tab). Match
  // rows arrive as "Name (fund match)" / "Name (match pledge)" — strip the
  // suffix so a person's matches count toward their total, like the app does
  // by UID. "Anonymous" never ranks.
  const totals = {};
  contributions.forEach(c => {
    const clean = (c.displayName || 'Anonymous').replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Anonymous';
    if (clean.toLowerCase() === 'anonymous') return;
    totals[clean] = (totals[clean] || 0) + (c.amount || 0);
  });
  const leaderboard = Object.entries(totals)
    .map(([n, t]) => ({ name: n, total: t }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 3);
  const showLeaderboard = Object.keys(totals).length >= 3;

  const RECENT_LIMIT = 10;
  const visibleContribs = expanded ? contributions : contributions.slice(0, RECENT_LIMIT);
  const hiddenCount = contributions.length - RECENT_LIMIT;

  return (
    <div style={s.page}>
      <div className="mesh" />
      <AndroidAppBar onGet={() => stashInvite(code.toUpperCase())} />
      <div style={s.wrap}>

        {/* Nav */}
        <nav style={s.nav} className="fade-up">
          <span style={s.navBrand} onClick={() => navigate('/')}>
            <LogoMark size={42} />
            <span style={s.navWordmark}>FundPot</span>
          </span>
          <a style={s.navLink} href="/">What is FundPot?</a>
        </nav>

        {/* Header */}
        <header style={s.header}>
          <div className="eyebrow fade-up">Private group fund</div>
          <h1 className="fade-up" style={{ ...s.fundName, animationDelay: '0.05s' }}>{fund.name}</h1>
          {fund.description && (
            <p className="fade-up" style={{ ...s.fundDesc, animationDelay: '0.1s' }}>{fund.description}</p>
          )}
        </header>

        {/* Get the app — kept high on the page so both badges are visible
            immediately, not buried under the fund content on mobile. */}
        <div className="glass fade-up" style={{ ...s.getAppTop, animationDelay: '0.11s' }}>
          <span style={s.getAppCopy}>
            <div style={s.getAppTitle}>Get the FundPot app</div>
            <div style={s.getAppSub}>Group chat, polls, and instant contribution alerts.</div>
          </span>
          <span style={s.getAppBadges}>
            <a className="badge-apple" href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => stashInvite(fund.shortCode)}>
              <img src="/appstore-badge.svg" alt="Download on the App Store" style={{ height: 44, display: 'block' }} />
            </a>
            <a className="play-badge" href={PLAY_STORE_URL} target="_blank" rel="noreferrer" onClick={() => stashInvite(fund.shortCode)} style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/google-play-badge-trimmed.png" alt="Get it on Google Play" style={{ height: 44, display: 'block' }} />
            </a>
          </span>
        </div>

        <div className="fund-grid">
          {/* ── Left column ── */}
          <div className="fund-col">
            {/* Balance */}
            <div className="glass fade-up" style={{ ...s.card, animationDelay: '0.12s' }}>
              <div style={s.balLabel}>Total collected</div>
              <div className="gradient-text" style={s.balAmt}>{money(fund.balance)}</div>
              {fund.goal > 0 && (
                <>
                  <div style={s.barWrap}>
                    <div style={{ ...s.barFill, width: `${progress * 100}%` }} />
                  </div>
                  <div style={s.goalRow}>
                    <span>{money(fund.balance)} of {money(fund.goal)} goal</span>
                    <span style={s.goalPct}>{Math.round(progress * 100)}%</span>
                  </div>
                </>
              )}
            </div>

            {/* Paid out */}
            {fund.status === 'paid_out' && (
              <div className="fade-up" style={{ ...s.banner, animationDelay: '0.16s' }}>
                {Ic.check} This fund has been paid out.
              </div>
            )}

            {/* Contributors */}
            {!isAnon && (
              <div className="glass fade-up" style={{ ...s.card, animationDelay: '0.18s' }}>
                <div style={s.cardTitle}>
                  <span style={s.iconChip}>{Ic.users}</span>
                  Contributors
                  <span style={s.countChip}>{fund.contributorCount || 0}</span>
                </div>
                {contributions.length === 0 ? (
                  <p style={s.emptyText}>No contributions yet. Be the first!</p>
                ) : (
                  <>
                    {showLeaderboard && (
                      <>
                        <div style={s.subHead}>Top contributors</div>
                        {leaderboard.map((p, i) => (
                          <div key={p.name} style={{ ...s.contribRow, ...(i === leaderboard.length - 1 ? { borderBottom: 'none' } : {}) }}>
                            <span style={{ ...s.rankChip, ...s.rankColors[i] }}>{i + 1}</span>
                            <span style={s.contribName}>{p.name}</span>
                            <span style={s.contribAmt}>{money(p.total)}</span>
                          </div>
                        ))}
                        <div style={s.topDivider} />
                        <div style={s.subHead}>Recent</div>
                      </>
                    )}
                    {visibleContribs.map((c, i) => (
                      <div key={c.id} style={{ ...s.contribRow, ...(i === visibleContribs.length - 1 ? { borderBottom: 'none', paddingBottom: 4 } : {}) }}>
                        <span style={s.avatar}>{(c.displayName || 'A').trim().charAt(0).toUpperCase()}</span>
                        <span style={s.contribName}>{c.displayName || 'Anonymous'}</span>
                        <span style={s.contribAmt}>+{money(c.amount)}</span>
                      </div>
                    ))}
                    {hiddenCount > 0 && (
                      <button className="cta-ghost" style={s.showAllBtn} onClick={() => setExpanded(x => !x)}>
                        {expanded ? 'Show recent only' : `Show all ${contributions.length} contributions`}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {isAnon && isActive && (
              <div className="fade-up" style={{ ...s.bannerMuted, animationDelay: '0.18s' }}>
                {Ic.lock} Contributions are anonymous for this fund.
              </div>
            )}
          </div>

          {/* ── Right column ── */}
          <div className="fund-col">
            {/* Card / Apple Pay / Google Pay */}
            {isActive && (
              <div className="glass fade-up" style={{ ...s.card, animationDelay: '0.1s' }}>
                <div style={s.cardTitle}>
                  <span style={s.iconChip}>{Ic.card}</span>
                  Contribute by card or Apple&nbsp;Pay
                </div>
                {returnedPaid
                  ? <div style={s.successMsg}>{Ic.check} Thank you! Your contribution has been added to the fund.</div>
                  : <WebPay fund={fund} onSuccess={refresh} />
                }
              </div>
            )}

            {/* Zelle */}
            {isActive && fund.zelleInfo && (
              <div className="glass fade-up" style={{ ...s.card, animationDelay: '0.14s' }}>
                <div style={s.cardTitle}>
                  <span style={s.iconChip}>{Ic.send}</span>
                  Send your contribution via Zelle
                </div>
                <div style={s.zelleVal}>{fund.zelleInfo}</div>
                <button className="cta-primary" style={s.copyBtn} onClick={handleCopyZelle}>
                  {copied ? 'Copied' : 'Copy Zelle Info'}
                </button>
                <span style={s.memoChip}>
                  Include <span style={s.memoCode}>{fund.shortCode}</span> in your memo so your payment is tracked automatically.
                </span>
                <p style={s.note}>
                  Zelle lives inside your bank's app — copy the info above, then open your
                  bank app and send via Zelle. Your deposit will appear here automatically
                  once detected.
                </p>
              </div>
            )}

          </div>
        </div>
      </div>

      <footer style={s.footer}>
        Powered by <strong style={{ color: 'var(--accent)' }}>FundPot</strong> · A product of Sark Labs LLC
      </footer>
    </div>
  );
}
